import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Grid, Tab, Tabs, Stack, Typography } from "@mui/material";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import ShowChart from "@mui/icons-material/ShowChart";
import moment from "moment";

// Import refactored components
import TrainingStatisticsCard from "./training/TrainingStatisticsCard";
import TodaysTrainingCard from "./training/TodaysTrainingCard";
import AnalyticsQuickCards from "./training/AnalyticsQuickCards";
import RecentWorkouts from "./training/RecentWorkouts";
import MonthYearPicker from "../../../../../components/molecules/MonthYearPicker";
import useClientExerciseReports from "../../../../../hooks/useClientExerciseReports";
import {
  getMonthRange,
  getMonthRangeFromKey,
} from "../../../../../utils/monthDateRange";

const TRAINING_SUB_OVERVIEW = "overview";
const TRAINING_SUB_HISTORY = "history";
const TODAY_MONTH_KEY = getMonthRange(new Date()).key;
// Group exercises by workout session (date-based) — defined outside component for stability
const groupExercisesByWorkout = (exercises) => {
  const workouts = {};
  (exercises || []).forEach((exercise) => {
    const exerciseDate = new Date(
      exercise.completedOn || exercise.createdOn,
    );

    // Skip exercises with invalid dates
    if (isNaN(exerciseDate.getTime())) {
      console.warn('Skipping exercise with invalid date:', exercise);
      return;
    }

    const dateKey = exerciseDate.toDateString();
    if (!workouts[dateKey]) {
      workouts[dateKey] = {
        id: dateKey,
        date: exerciseDate.toISOString(),
        exercises: [],
        completed: true,
        totalSets: 0,
        completedSets: 0,
      };
    }
    workouts[dateKey].exercises.push(exercise);
    workouts[dateKey].totalSets += exercise.sets?.length || 0;
    workouts[dateKey].completedSets +=
      exercise.sets?.filter((set) => set.completed).length || 0;
    if (!exercise.completed) {
      workouts[dateKey].completed = false;
    }
  });
  return Object.values(workouts);
};

// Enhanced Training Tab Component
const EnhancedTrainingTab = ({
  exerciseData: overviewExerciseData,
  currentWorkout,
  chartType,
  setChartType,
  clientId,
  organizationId,
}) => {
  const [expandedWorkouts, setExpandedWorkouts] = useState(new Set());
  const [trainingSubTab, setTrainingSubTab] = useState(TRAINING_SUB_OVERVIEW);
  const [historyMonthKey, setHistoryMonthKey] = useState(TODAY_MONTH_KEY);
  const prevTrainingSubTab = useRef(trainingSubTab);

  const historyRange = useMemo(
    () => getMonthRangeFromKey(historyMonthKey),
    [historyMonthKey],
  );

  const { exerciseData: historyExerciseData } = useClientExerciseReports({
    uid: clientId,
    organizationId,
    start: historyRange.start,
    end: historyRange.end,
  });

  // Overview uses current-month parent data; history uses selected month
  const exerciseData =
    trainingSubTab === TRAINING_SUB_HISTORY
      ? historyExerciseData
      : overviewExerciseData;

  // Entering History & charts opens Chart View so graphs are visible immediately
  useEffect(() => {
    const prev = prevTrainingSubTab.current;
    prevTrainingSubTab.current = trainingSubTab;
    if (
      prev === TRAINING_SUB_OVERVIEW &&
      trainingSubTab === TRAINING_SUB_HISTORY &&
      typeof setChartType === "function"
    ) {
      setChartType("chart");
    }
  }, [trainingSubTab, setChartType]);

  // Toggle expanded state for workout details
  const toggleWorkoutExpansion = (workoutId) => {
    const newExpanded = new Set(expandedWorkouts);
    if (newExpanded.has(workoutId)) {
      newExpanded.delete(workoutId);
    } else {
      newExpanded.add(workoutId);
    }
    setExpandedWorkouts(newExpanded);
  };

  // Calculate training statistics from real data
  const completedWorkouts =
    exerciseData?.filter((ex) => ex.completed).length || 0;
  const totalWorkouts = exerciseData?.length || 0;
  const completionRate = totalWorkouts
    ? ((completedWorkouts / totalWorkouts) * 100).toFixed(1)
    : 0;

  // Use currentWorkout directly - it contains reportData and other properties
  const todaysWorkout = currentWorkout;
  const reportData = currentWorkout?.reportData;
  const exercisesFromReport = reportData?.exercises || [];
  const workoutStatus = currentWorkout?.status || "incomplete";

  // Find active exercise (incomplete one)
  const activeExercise =
    exercisesFromReport.find((ex) => !ex.completed) ||
    exercisesFromReport.find((ex) => ex.sets.some((set) => !set.completed));

  // Find active set within the active exercise
  const activeSet = activeExercise?.sets?.find((set) => !set.completed);
  const activeSetIndex =
    activeExercise?.sets?.findIndex((set) => !set.completed) + 1;

  // Calculate overall workout progress
  const totalSets = exercisesFromReport.reduce(
    (sum, ex) => sum + (ex.sets?.length || 0),
    0,
  );
  const completedSets = exercisesFromReport.reduce(
    (sum, ex) => sum + (ex.sets?.filter((set) => set.completed).length || 0),
    0,
  );

  // Calculate time elapsed from currentWorkout
  const timeElapsed = React.useMemo(() => {
    if (!todaysWorkout || workoutStatus === "complete") return null;

    let startTime = null;

    // Handle different date formats (Firestore timestamp or Date)
    if (todaysWorkout.createdOn) {
      if (todaysWorkout.createdOn.toDate) {
        // Firestore timestamp
        startTime = todaysWorkout.createdOn.toDate();
      } else {
        // Regular Date object
        startTime = new Date(todaysWorkout.createdOn);
      }
    }

    if (!startTime) return null;

    const now = new Date();
    const elapsed = moment(now).diff(moment(startTime), "minutes");

    return elapsed;
  }, [todaysWorkout, workoutStatus]);

  // Calculate weekly workouts from the data
  const weeklyWorkouts = exerciseData
    ? exerciseData.filter((ex) => {
        const exerciseDate = new Date(ex.createdOn || ex.date);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return exerciseDate >= weekAgo;
      }).length
    : 0;

  // Build workout sessions for the selected history month (all sessions in range)
  const workoutSessions = React.useMemo(() => {
    const allSessions = groupExercisesByWorkout(exerciseData);
    return allSessions.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [exerciseData]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          mx: -3,
          mt: -3,
          px: 3,
          pt: 0,
        }}
      >
        <Tabs
          value={trainingSubTab}
          onChange={(_, v) => setTrainingSubTab(v)}
          variant="fullWidth"
          aria-label="Training sections"
          sx={{
            minHeight: 52,
            "& .MuiTab-root": {
              minHeight: 52,
              textTransform: "none",
              fontWeight: 600,
            },
          }}
        >
          <Tab
            value={TRAINING_SUB_OVERVIEW}
            icon={<DashboardOutlined fontSize="small" />}
            iconPosition="start"
            label="Overview"
          />
          <Tab
            value={TRAINING_SUB_HISTORY}
            icon={<ShowChart fontSize="small" />}
            iconPosition="start"
            label="History & charts"
          />
        </Tabs>
      </Box>

      {trainingSubTab === TRAINING_SUB_OVERVIEW && (
        <Grid container spacing={3}>
          {/* Full-width stack avoids mismatched column heights (short stats vs tall session) */}
          <Grid item xs={12}>
            <TrainingStatisticsCard
              totalWorkouts={totalWorkouts}
              completedWorkouts={completedWorkouts}
              weeklyWorkouts={weeklyWorkouts}
              completionRate={completionRate}
            />
          </Grid>

          <Grid item xs={12}>
            <TodaysTrainingCard
              todaysWorkout={todaysWorkout}
              workoutStatus={workoutStatus}
              activeExercise={activeExercise}
              timeElapsed={timeElapsed}
              exercisesFromReport={exercisesFromReport}
              completedSets={completedSets}
              totalSets={totalSets}
              reportData={reportData}
              activeSet={activeSet}
              activeSetIndex={activeSetIndex}
              currentWorkout={currentWorkout}
            />
          </Grid>

          <Grid item xs={12}>
            <AnalyticsQuickCards
              exercisesFromReport={exercisesFromReport}
              completedSets={completedSets}
              totalSets={totalSets}
              workoutStatus={workoutStatus}
            />
          </Grid>
        </Grid>
      )}

      {trainingSubTab === TRAINING_SUB_HISTORY && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={2}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Workout history
              </Typography>
              <MonthYearPicker
                value={historyMonthKey}
                onChange={setHistoryMonthKey}
                showToday
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <RecentWorkouts
              workoutSessions={workoutSessions}
              chartType={chartType}
              setChartType={setChartType}
              expandedWorkouts={expandedWorkouts}
              toggleWorkoutExpansion={toggleWorkoutExpansion}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default EnhancedTrainingTab;
